import { useState, useEffect } from "react";
import {
  Box,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Button
} from "@material-ui/core";
import { toast } from "react-toastify";
import { HugeiconsIcon } from "@hugeicons/react";
import { Comment01Icon, PencilEdit02Icon } from "@hugeicons-pro/core-stroke-rounded";
import ThumbUpAltOutlined from "@material-ui/icons/ThumbUpAltOutlined";
import ThumbUp from "@material-ui/icons/ThumbUp";
import ThumbDownAltOutlined from "@material-ui/icons/ThumbDownAltOutlined";
import ThumbDown from "@material-ui/icons/ThumbDown";
import { useAgentChat } from "./AgentChatProvider";
import { useUser } from "../../hooks/user";
import * as mainframeApi from "./mainframeApi";
import useStyles from "./styles";

const CONTENT_TYPE = "analytics_conversation";
const USER_TYPE = "Boom::Customer";

export default function SessionFeedback() {
  const classes = useStyles();
  const { token, activeSessionId, activeSession } = useAgentChat();
  const { user } = useUser();

  const [feedbackType, setFeedbackType] = useState(null);
  const [feedbackText, setFeedbackText] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);

  // Draft state for the dialog form
  const [draftType, setDraftType] = useState(null);
  const [draftText, setDraftText] = useState("");

  const hasAssistantMessage =
    activeSession?.messages?.some((m) => m.role === "assistant") ?? false;
  const canInteract =
    hasAssistantMessage && !isLoading && activeSessionId;

  // Fetch existing feedback when session changes
  useEffect(() => {
    if (!token || !activeSessionId || !user?.id) {
      setIsLoading(false);
      return;
    }

    let cancelled = false;
    setIsLoading(true);
    setFeedbackType(null);
    setFeedbackText("");

    mainframeApi
      .getFeedback(token, CONTENT_TYPE, activeSessionId, user.id)
      .then((data) => {
        if (cancelled) return;
        if (data?.feedback_type) setFeedbackType(data.feedback_type);
        if (data?.feedback_text) setFeedbackText(data.feedback_text);
      })
      .catch((err) => {
        console.error("Failed to fetch feedback:", err);
      })
      .finally(() => {
        if (!cancelled) setIsLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [token, activeSessionId, user?.id]);

  const handleOpen = () => {
    setDraftType(feedbackType);
    setDraftText(feedbackText);
    setDialogOpen(true);
  };

  const handleClose = () => {
    setDialogOpen(false);
  };

  const handleSubmit = async () => {
    if (!draftType || !user?.id) return;
    setIsSubmitting(true);
    try {
      await mainframeApi.submitFeedback(token, {
        content_type: CONTENT_TYPE,
        content_id: activeSessionId,
        user_id: user.id,
        user_type: USER_TYPE,
        feedback_type: draftType,
        feedback_text: draftText || undefined
      });
      setFeedbackType(draftType);
      setFeedbackText(draftText);
      setDialogOpen(false);
      toast.success("Feedback submitted");
    } catch (err) {
      console.error("Failed to submit feedback:", err);
      toast.error("Failed to submit feedback");
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <>
      {canInteract && (
        <Button
          className={classes.feedbackButton}
          size="small"
          onClick={handleOpen}
          startIcon={
            <HugeiconsIcon
              icon={feedbackType ? PencilEdit02Icon : Comment01Icon}
              size={20}
            />
          }
        >
          {feedbackType ? "Edit feedback" : "Provide feedback"}
        </Button>
      )}

      <Dialog open={dialogOpen} onClose={handleClose} maxWidth="sm" fullWidth>
        <DialogTitle>Session Feedback</DialogTitle>
        <DialogContent>
          <Box display="flex" className={classes.feedbackThumbRow}>
            <IconButton
              className={
                draftType === "thumbs_up" ? classes.feedbackActive : ""
              }
              onClick={() => setDraftType("thumbs_up")}
              aria-label="Thumbs up"
              aria-pressed={draftType === "thumbs_up"}
            >
              {draftType === "thumbs_up" ? (
                <ThumbUp />
              ) : (
                <ThumbUpAltOutlined />
              )}
            </IconButton>
            <IconButton
              className={
                draftType === "thumbs_down" ? classes.feedbackActive : ""
              }
              onClick={() => setDraftType("thumbs_down")}
              aria-label="Thumbs down"
              aria-pressed={draftType === "thumbs_down"}
            >
              {draftType === "thumbs_down" ? (
                <ThumbDown />
              ) : (
                <ThumbDownAltOutlined />
              )}
            </IconButton>
          </Box>
          <TextField
            fullWidth
            multiline
            rows={3}
            variant="outlined"
            placeholder="Tell us more... (optional)"
            value={draftText}
            onChange={(e) => setDraftText(e.target.value)}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={handleClose}>Cancel</Button>
          <Button
            variant="contained"
            color="primary"
            onClick={handleSubmit}
            disabled={!draftType || isSubmitting}
          >
            Submit
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
}
